import { appendPlural, decodeString } from "@findemdev/findem-helpers";
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchExecProjectsQuery } from "../../../services/execProjects";
import type {
  IExecProject,
  IExecProjectListItem,
  IExecProjectSearchFilter,
  IExecProjectSearchParams,
  IExecProjectSearchResponse,
  IExecProjectSortField,
} from "../../../types/agent-pipelines";
import {
  ALL_ASSIGNEES_FILTER_VALUE,
  ALL_CLIENTS_FILTER_VALUE,
  ITEMS_PER_PAGE,
  STATUS_FILTER_OPTIONS,
  STATUS_FILTER_TO_API,
} from "../constants";
import {
  getRaasAccountDefaultJoid,
  getRaasOrgOptions,
  getRaasUserOptions,
  getRaasUsersById,
  withRaasUnresolvedUserOption,
} from "../identity";
import {
  getRaasPortalDefinition,
  hasRaasPortalProjectFormField,
  hasRaasPortalTableColumn,
  hasRaasPortalToolbarControl,
} from "../portalConfig";
import { RaasProjectListBackendStatus } from "../status";
import type {
  IFilterOption,
  IProjectRow,
  ISortConfig,
  SortableColumnKey,
  SortOrder,
  StatusFilter,
} from "../types";
import { SORTABLE_COLUMN_API_FIELD } from "../types";
import {
  getFirstName,
  getGreeting,
  isRaasInsufficientPermissionsError,
  getVisiblePages,
} from "../utils";
import { getRaasProjectCollaboration } from "../collaboration";
import { useRaasClientPortalRows } from "./useRaasClientPortalRows";
import { useRaasPortalIdentity } from "./useRaasPortalIdentity";
import { useRaasProjectActions } from "./useRaasProjectActions";

const DEFAULT_PROJECT_SORT_CONFIG: ISortConfig = {
  field: "updated_at",
  order: "desc",
};

const APPROVED_STATUS_FILTER: StatusFilter = "completed";
const DEFAULT_PROJECT_SEARCH_SORT_FIELDS: IExecProjectSortField[] = [
  { field: "updated_at", order: "asc" },
];

const getStatusFilterValues = ({
  statusFilter,
}: {
  statusFilter: StatusFilter;
}): RaasProjectListBackendStatus[] => {
  if (statusFilter === "all") return [];
  if (statusFilter === APPROVED_STATUS_FILTER) {
    return [
      RaasProjectListBackendStatus.Approved,
      RaasProjectListBackendStatus.Completed,
    ];
  }

  return [STATUS_FILTER_TO_API[statusFilter]];
};

const getProjectSearchFilters = ({
  clientFilter,
  searchTerm,
  status,
}: {
  clientFilter: string;
  searchTerm: string;
  status?: RaasProjectListBackendStatus;
}): IExecProjectSearchFilter[] => {
  const filters: IExecProjectSearchFilter[] = [];

  if (searchTerm) {
    filters.push({
      field: "project_name",
      operator: "contains",
      value: searchTerm,
    });
  }
  if (status) {
    filters.push({ field: "status", operator: "equals", value: status });
  }
  if (clientFilter !== ALL_CLIENTS_FILTER_VALUE) {
    filters.push({
      field: "client_company_id",
      operator: "equals",
      value: clientFilter,
    });
  }
  return filters;
};

const getPrimaryProjectSearchParams = ({
  assigneeId,
  cursor,
  currentPage,
  filters,
  isApprovedStatusFilter,
  joid,
  sortFields,
}: {
  assigneeId?: string;
  cursor?: string;
  currentPage: number;
  filters: IExecProjectSearchFilter[];
  isApprovedStatusFilter: boolean;
  joid: string;
  sortFields?: IExecProjectSortField[];
}): IExecProjectSearchParams => {
  if (assigneeId) {
    return {
      cross_account: true,
      filters: [
        {
          field: "acting_user_id",
          operator: "equals",
          value: assigneeId,
        },
      ],
      joid,
      ...(cursor ? { cursor } : {}),
      page: 0,
    };
  }

  return {
    joid,
    page: isApprovedStatusFilter ? 0 : currentPage - 1,
    size: isApprovedStatusFilter
      ? currentPage * ITEMS_PER_PAGE
      : ITEMS_PER_PAGE,
    ...(filters.length ? { filters } : {}),
    ...(sortFields ? { sort_fields: sortFields } : {}),
  };
};

const getProjectSortValue = ({
  field,
  project,
}: {
  field: string;
  project: IExecProjectListItem;
}) =>
  String((project as unknown as Record<string, unknown>)[field] ?? "");

const compareProjectSortValues = ({
  leftValue,
  order,
  rightValue,
}: {
  leftValue: string;
  order: IExecProjectSortField["order"];
  rightValue: string;
}) => {
  const comparison = leftValue.localeCompare(rightValue, undefined, {
    numeric: true,
    sensitivity: "base",
  });

  return order === "desc" ? -comparison : comparison;
};

const compareProjectSearchItems = ({
  left,
  right,
  sortFields,
}: {
  left: IExecProjectListItem;
  right: IExecProjectListItem;
  sortFields: IExecProjectSortField[];
}) => {
  for (const sortField of sortFields) {
    const comparison = compareProjectSortValues({
      leftValue: getProjectSortValue({ field: sortField.field, project: left }),
      order: sortField.order,
      rightValue: getProjectSortValue({ field: sortField.field, project: right }),
    });

    if (comparison !== 0) return comparison;
  }

  return 0;
};

const getMergedApprovedProjectSearchData = ({
  completedData,
  currentPage,
  primaryData,
  sortFields,
}: {
  completedData?: IExecProjectSearchResponse;
  currentPage: number;
  primaryData?: IExecProjectSearchResponse;
  sortFields: IExecProjectSortField[];
}): IExecProjectSearchResponse | undefined => {
  if (!primaryData && !completedData) return undefined;

  const projects = [
    ...(primaryData?.projects ?? []),
    ...(completedData?.projects ?? []),
  ].sort((left, right) =>
    compareProjectSearchItems({ left, right, sortFields }),
  );
  const start = (currentPage - 1) * ITEMS_PER_PAGE;

  return {
    count: (primaryData?.count ?? 0) + (completedData?.count ?? 0),
    projects: projects.slice(start, start + ITEMS_PER_PAGE),
  };
};

const normalizeProjectFilterValue = (value: string) =>
  decodeString(value).trim().toLocaleLowerCase();

const getLocallyTransformedCrossAccountProjects = ({
  clientFilter,
  projects,
  searchTerm,
  sortFields,
  statusFilterValues,
}: {
  clientFilter: string;
  projects?: IExecProjectListItem[];
  searchTerm: string;
  sortFields?: IExecProjectSortField[];
  statusFilterValues: RaasProjectListBackendStatus[];
}) => {
  const normalizedSearchTerm = normalizeProjectFilterValue(searchTerm);
  const filteredProjects = (projects ?? []).filter((project) => {
    if (
      normalizedSearchTerm &&
      !normalizeProjectFilterValue(project.project_name).includes(normalizedSearchTerm)
    ) {
      return false;
    }
    if (
      statusFilterValues.length > 0 &&
      !statusFilterValues.some((status) => status === project.status)
    ) {
      return false;
    }
    if (
      clientFilter !== ALL_CLIENTS_FILTER_VALUE &&
      project.client_company_id !== clientFilter
    ) {
      return false;
    }

    return true;
  });

  if (!sortFields?.length) return filteredProjects;

  return filteredProjects.sort((left, right) =>
    compareProjectSearchItems({ left, right, sortFields }),
  );
};

export const useRaasClientPortalController = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const {
    allOrgs,
    allUsers,
    canUseImpersonationSelector,
    canUseOrgSelector,
    currentUser,
    isFetchingAllOrgs,
    isFetchingAllUsers,
    isProjectPlatformResolutionPending,
    joid,
    platformType,
    searchParams,
    selectedAccountDefaultJoid,
    selectedAccountId,
    selectedJoid,
  } = useRaasPortalIdentity();
  const portalDefinition = getRaasPortalDefinition({ pathname });
  const hasClientFilterControl = hasRaasPortalToolbarControl({
    controlKey: "client_filter",
    portalDefinition,
  });
  const hasAssignedToFilterControl =
    canUseImpersonationSelector &&
    hasRaasPortalToolbarControl({
      controlKey: "assigned_to_filter",
      portalDefinition,
    });
  const includesClientDetails =
    hasClientFilterControl ||
    hasRaasPortalTableColumn({ columnKey: "client", portalDefinition }) ||
    hasRaasPortalProjectFormField({ fieldKey: "client", portalDefinition });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [clientFilter, setClientFilter] = useState(ALL_CLIENTS_FILTER_VALUE);
  const [clientFilterOptions, setClientFilterOptions] = useState<IFilterOption[]>([
    { label: "All Clients", value: ALL_CLIENTS_FILTER_VALUE },
  ]);
  const [assignedToFilter, setAssignedToFilter] = useState(
    ALL_ASSIGNEES_FILTER_VALUE,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [crossAccountCursorsByPage, setCrossAccountCursorsByPage] = useState<
    Record<number, string>
  >({});
  const [crossAccountFirstPageCount, setCrossAccountFirstPageCount] = useState(0);
  const [sortConfig, setSortConfig] = useState<ISortConfig | null>(
    DEFAULT_PROJECT_SORT_CONFIG,
  );

  const effectiveClientFilter = hasClientFilterControl ? clientFilter : ALL_CLIENTS_FILTER_VALUE;
  const effectiveAssignedToFilter = hasAssignedToFilterControl
    ? assignedToFilter
    : ALL_ASSIGNEES_FILTER_VALUE;
  const crossAccountAssigneeId =
    effectiveAssignedToFilter === ALL_ASSIGNEES_FILTER_VALUE
      ? undefined
      : effectiveAssignedToFilter;
  const isCrossAccountAssigneeSearch = Boolean(crossAccountAssigneeId);
  const [visibleProjectRefs, setVisibleProjectRefs] = useState<string[]>([]);

  const statusFilterValues = useMemo(
    () => getStatusFilterValues({ statusFilter }),
    [statusFilter],
  );
  const primaryStatusFilterValue = statusFilterValues[0];
  const secondaryStatusFilterValue = statusFilterValues[1];

  const apiFilters = useMemo(
    () =>
      getProjectSearchFilters({
        clientFilter: effectiveClientFilter,
        searchTerm: debouncedSearchTerm,
        status: primaryStatusFilterValue,
      }),
    [
      debouncedSearchTerm,
      effectiveClientFilter,
      primaryStatusFilterValue,
    ],
  );
  const secondaryApiFilters = useMemo(
    () =>
      getProjectSearchFilters({
        clientFilter: effectiveClientFilter,
        searchTerm: debouncedSearchTerm,
        status: secondaryStatusFilterValue,
      }),
    [
      debouncedSearchTerm,
      effectiveClientFilter,
      secondaryStatusFilterValue,
    ],
  );

  // Build sort_fields: active column sort first, then updated_at desc as tiebreaker
  const sortFields = useMemo(() => {
    if (!sortConfig) return undefined;
    const primary = {
      field: SORTABLE_COLUMN_API_FIELD[sortConfig.field],
      order: sortConfig.order,
    };
    if (sortConfig.field === "updated_at") return [primary];
    return [primary, { field: "updated_at", order: "desc" as const }];
  }, [sortConfig]);

  const isApprovedStatusFilter = statusFilter === APPROVED_STATUS_FILTER;
  const approvedStatusRequestSize = currentPage * ITEMS_PER_PAGE;
  const primaryProjectSearchParams = getPrimaryProjectSearchParams({
    assigneeId: crossAccountAssigneeId,
    cursor: isCrossAccountAssigneeSearch
      ? crossAccountCursorsByPage[currentPage]
      : undefined,
    currentPage,
    filters: apiFilters,
    isApprovedStatusFilter,
    joid: joid ?? "",
    sortFields,
  });

  const {
    data,
    error,
    isError,
    isFetching,
    isLoading,
  } = useSearchExecProjectsQuery(
    primaryProjectSearchParams,
    { skip: !joid },
  );
  const {
    data: secondaryData,
    error: secondaryError,
    isError: isSecondaryError,
    isFetching: isSecondaryFetching,
    isLoading: isSecondaryLoading,
  } = useSearchExecProjectsQuery(
    {
      joid: joid ?? "",
      page: 0,
      size: approvedStatusRequestSize,
      ...(secondaryApiFilters.length
        ? { filters: secondaryApiFilters }
        : {}),
      ...(sortFields ? { sort_fields: sortFields } : {}),
    },
    {
      skip:
        !joid ||
        isCrossAccountAssigneeSearch ||
        !secondaryStatusFilterValue,
    },
  );
  const mergedData = useMemo(
    () => {
      if (isCrossAccountAssigneeSearch || !isApprovedStatusFilter) return data;

      return getMergedApprovedProjectSearchData({
        completedData: secondaryData,
        currentPage,
        primaryData: data,
        sortFields: sortFields ?? DEFAULT_PROJECT_SEARCH_SORT_FIELDS,
      });
    },
    [
      currentPage,
      data,
      isApprovedStatusFilter,
      isCrossAccountAssigneeSearch,
      secondaryData,
      sortFields,
    ],
  );
  const isSecondaryProjectSearchActive =
    !isCrossAccountAssigneeSearch && Boolean(secondaryStatusFilterValue);
  const projectSearchError =
    error || (isSecondaryProjectSearchActive ? secondaryError : undefined);
  const isProjectSearchError =
    isError || (isSecondaryProjectSearchActive && isSecondaryError);
  const isProjectSearchFetching =
    isFetching || (isSecondaryProjectSearchActive && isSecondaryFetching);
  const isProjectSearchLoading =
    isLoading || (isSecondaryProjectSearchActive && isSecondaryLoading);
  const isAccessDeniedError = isRaasInsufficientPermissionsError({ error: projectSearchError });

  useEffect(() => {
    if (
      !isCrossAccountAssigneeSearch ||
      currentPage !== 1 ||
      isProjectSearchFetching ||
      mergedData?.count === undefined
    ) {
      return;
    }

    setCrossAccountFirstPageCount(mergedData.count);
  }, [
    currentPage,
    isCrossAccountAssigneeSearch,
    isProjectSearchFetching,
    mergedData?.count,
  ]);

  const navigateToProject = useCallback(
    ({ project }: { project: IExecProject }) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      const { isCollaborator } = getRaasProjectCollaboration({ currentUser, project });
      const projectJoid = isCollaborator
        ? currentUser.uid
        : project.created_by_uid || joid || currentUser.uid;
      if (projectJoid) {
        nextSearchParams.set("joid", projectJoid);
      }
      if (project.account_id) {
        nextSearchParams.set("account_id", project.account_id);
      }

      const query = nextSearchParams.toString();
      navigate(
        `${portalDefinition.basePath}/projects/${project.ref}${query ? `?${query}` : ""}`,
      );
    },
    [currentUser, joid, navigate, portalDefinition.basePath, searchParams],
  );

  const handleProjectCreated = useCallback(
    ({ project }: { project: IExecProject }) => {
      navigateToProject({ project });
    },
    [navigateToProject],
  );

  const projectOwnerUser = allUsers?.users.find((user) => user._id === joid);
  const projectOwnerOrgUser = allOrgs?.find((user) => user.id === joid);
  const projectClientAccountId =
    projectOwnerUser?.account_id ||
    projectOwnerOrgUser?.account_id ||
    currentUser.account_id;
  const projectClientLabel =
    projectOwnerUser?.company_name ||
    projectOwnerUser?.local.company_name ||
    projectOwnerOrgUser?.org_name ||
    currentUser.company;

  const projectActions = useRaasProjectActions({
    currentUserAccountId: projectClientAccountId,
    defaultClientLabel: projectClientLabel,
    isProjectPlatformResolutionPending,
    joid,
    onProjectCreated: handleProjectCreated,
    platformType,
    projectFormFieldKeys: portalDefinition.projectFormFieldKeys,
  });

  // Separate unfiltered query so the headline count ("X projects need your review")
  // always reflects all projects, not just the current page.
  const { data: attentionData } = useSearchExecProjectsQuery(
    { joid: joid ?? "", page: 0, size: 1 },
    { skip: !joid },
  );
  const attentionCount = attentionData?.count ?? 0;

  const projectsForRows = useMemo(
    () => isCrossAccountAssigneeSearch
      ? getLocallyTransformedCrossAccountProjects({
        clientFilter: effectiveClientFilter,
        projects: mergedData?.projects,
        searchTerm: debouncedSearchTerm,
        sortFields,
        statusFilterValues,
      })
      : mergedData?.projects,
    [
      debouncedSearchTerm,
      effectiveClientFilter,
      isCrossAccountAssigneeSearch,
      mergedData?.projects,
      sortFields,
      statusFilterValues,
    ],
  );

  const { rows, companyDetailsByMetaRef } = useRaasClientPortalRows({
    allOrgs,
    allUsers,
    enrichmentScopeKey: isCrossAccountAssigneeSearch
      ? String(currentPage)
      : undefined,
    includeClientDetails: includesClientDetails,
    joid: isCrossAccountAssigneeSearch ? effectiveAssignedToFilter : joid,
    projects: projectsForRows,
    visibleProjectRefs: isCrossAccountAssigneeSearch
      ? visibleProjectRefs
      : undefined,
    viewerCompanyName: currentUser.company,
  });

  // Normal searches keep page-number pagination. Cross-account searches use
  // one server cursor for each page that the user has visited. Only their first
  // page returns the exact count, so retain it for the rest of the traversal.
  const totalCount = isCrossAccountAssigneeSearch
    ? crossAccountFirstPageCount
    : mergedData?.count ?? 0;
  const crossAccountPageSize =
    Number.isInteger(mergedData?.page_size) && (mergedData?.page_size ?? 0) > 0
      ? mergedData?.page_size
      : undefined;
  const totalPages = isCrossAccountAssigneeSearch
    ? totalCount === 0
      ? 0
      : crossAccountPageSize
        ? Math.ceil(totalCount / crossAccountPageSize)
        : mergedData?.next_cursor
          ? currentPage + 1
          : currentPage
    : Math.ceil(totalCount / ITEMS_PER_PAGE);
  const safeCurrentPage = isCrossAccountAssigneeSearch
    ? currentPage
    : Math.max(1, Math.min(currentPage, Math.max(totalPages, 1)));
  const canGoNext = isCrossAccountAssigneeSearch
    ? Boolean(mergedData?.next_cursor)
    : undefined;

  // Reset to page 1 when a search, status, or client filter value changes.
  // Primitive dependencies avoid resets from a new useMemo array reference.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, effectiveClientFilter]);

  useEffect(() => {
    if (currentPage !== safeCurrentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [currentPage, safeCurrentPage]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const isInitialProjectsLoading = isProjectSearchLoading || (isProjectSearchFetching && rows.length === 0);
  const attentionCountLabel = isInitialProjectsLoading ? "-" : attentionCount;
  const projectCountForGrammar = isInitialProjectsLoading ? 0 : attentionCount;
  const projectNoun = `project${appendPlural(projectCountForGrammar)}`;
  const headlineSuffix =
    projectCountForGrammar === 1 ? "needs your review" : "need your review";

  const visiblePages = useMemo(
    () => isCrossAccountAssigneeSearch
      ? [safeCurrentPage]
      : getVisiblePages({ currentPage: safeCurrentPage, totalPages }),
    [isCrossAccountAssigneeSearch, safeCurrentPage, totalPages],
  );
  const statusFilterOptions = useMemo(() => STATUS_FILTER_OPTIONS.map((opt) =>
    opt.value === "submitted_to_client"
      ? { ...opt, label: includesClientDetails ? "Submitted to Client" : "Ready for Approval" }
      : opt
  ), [includesClientDetails]);

  useEffect(() => {
    if (!hasClientFilterControl) {
      setClientFilterOptions([{ label: "All Clients", value: ALL_CLIENTS_FILTER_VALUE }]);
      return;
    }
    // Only rebuild the options list when no client is selected (full unfiltered data)
    if (effectiveClientFilter !== ALL_CLIENTS_FILTER_VALUE) return;

    const optionsByValue = new Map<string, IFilterOption>();
    rows.forEach((row) => {
      if (!row.clientFilterValue || row.clientLabel === "-") return;
      optionsByValue.set(row.clientFilterValue, {
        label: decodeString(row.clientLabel),
        value: row.clientFilterValue,
      });
    });

    setClientFilterOptions([
      { label: "All Clients", value: ALL_CLIENTS_FILTER_VALUE },
      ...Array.from(optionsByValue.values()).sort((left, right) =>
        left.label.localeCompare(right.label),
      ),
    ]);
  }, [effectiveClientFilter, hasClientFilterControl, rows]);

  useEffect(() => {
    if (!hasClientFilterControl && clientFilter !== ALL_CLIENTS_FILTER_VALUE) {
      setClientFilter(ALL_CLIENTS_FILTER_VALUE);
    }
  }, [clientFilter, hasClientFilterControl]);

  useEffect(() => {
    if (
      !hasAssignedToFilterControl &&
      assignedToFilter !== ALL_ASSIGNEES_FILTER_VALUE
    ) {
      setAssignedToFilter(ALL_ASSIGNEES_FILTER_VALUE);
    }
  }, [assignedToFilter, hasAssignedToFilterControl]);

  const handleRowClick = useCallback(
    ({ project }: { project: IProjectRow["project"] }) => {
      navigateToProject({ project });
    },
    [navigateToProject],
  );

  const handleRowKeyDown = useCallback(
    ({
      event,
      project,
    }: {
      event: KeyboardEvent<HTMLElement>;
      project: IProjectRow["project"];
    }) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleRowClick({ project });
      }
    },
    [handleRowClick],
  );

  const handleNextPage = useCallback(() => {
    if (isCrossAccountAssigneeSearch) {
      if (isProjectSearchFetching) return;
      const nextCursor = mergedData?.next_cursor;
      if (!nextCursor) return;

      const nextPage = currentPage + 1;
      setCrossAccountCursorsByPage((currentCursors) => ({
        ...currentCursors,
        [nextPage]: nextCursor,
      }));
      setVisibleProjectRefs([]);
      setCurrentPage(nextPage);
      return;
    }

    setCurrentPage((prev) => Math.min(prev + 1, Math.max(totalPages, 1)));
  }, [
    currentPage,
    isCrossAccountAssigneeSearch,
    isProjectSearchFetching,
    mergedData?.next_cursor,
  ]);

  const handlePreviousPage = useCallback(() => {
    if (isCrossAccountAssigneeSearch) {
      setVisibleProjectRefs([]);
    }
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, [isCrossAccountAssigneeSearch]);

  const handlePageClick = useCallback(
    ({ pageNumber }: { pageNumber: number }) => {
      if (isCrossAccountAssigneeSearch) return;
      setCurrentPage(Math.max(1, Math.min(pageNumber, Math.max(totalPages, 1))));
    },
    [isCrossAccountAssigneeSearch, totalPages],
  );

  const handleSearchTermChange = useCallback(({ value }: { value: string }) => {
    setSearchTerm(value);
  }, []);

  const handleStatusFilterChange = useCallback(({ value }: { value: StatusFilter }) => {
    setStatusFilter(value);
  }, []);
  const handleClientFilterChange = useCallback(({ value }: { value: string }) => {
    setClientFilter(value);
  }, []);
  const handleAssignedToFilterChange = useCallback(
    ({ value }: { value: string }) => {
      setAssignedToFilter(value);
      setCurrentPage(1);
      setCrossAccountCursorsByPage({});
      setCrossAccountFirstPageCount(0);
      setVisibleProjectRefs([]);
      if (value !== ALL_ASSIGNEES_FILTER_VALUE) {
        setSearchTerm("");
        setDebouncedSearchTerm("");
        setStatusFilter("all");
        setClientFilter(ALL_CLIENTS_FILTER_VALUE);
        setSortConfig(DEFAULT_PROJECT_SORT_CONFIG);
      }
    },
    [],
  );
  const handleVisibleProjectRefsChange = useCallback(
    ({ projectRefs }: { projectRefs: string[] }) => {
      setVisibleProjectRefs((currentProjectRefs) =>
        currentProjectRefs.length === projectRefs.length &&
        currentProjectRefs.every(
          (projectRef, index) => projectRef === projectRefs[index],
        )
          ? currentProjectRefs
          : projectRefs,
      );
    },
    [],
  );
  const handleBackToAccessibleProjects = useCallback(() => {
    navigate(portalDefinition.basePath);
  }, [navigate, portalDefinition.basePath]);
  const selectedAccountForPicker = selectedAccountId || currentUser.account_id;

  // Switching orgs rescopes `userOptions`, so a previously selected assignee can
  // stop being representable. Impersonation deliberately does NOT reset it:
  // changing `joid` moves the `created_by_uid` scope, a different axis, and the
  // selection stays meaningful there even if it matches nothing. The user
  // change handler still starts cursor pagination again from page 1.
  useEffect(() => {
    setAssignedToFilter(ALL_ASSIGNEES_FILTER_VALUE);
    setCurrentPage(1);
    setCrossAccountCursorsByPage({});
    setCrossAccountFirstPageCount(0);
    setVisibleProjectRefs([]);
  }, [selectedAccountForPicker]);
  const getDefaultJoidForAccount = useCallback(
    ({ accountId }: { accountId?: string | null }) =>
      getRaasAccountDefaultJoid({
        accountId,
        allOrgs,
        allUsers,
        currentUserAccountId: currentUser.account_id,
        currentUserId: currentUser.uid,
      }),
    [allOrgs, allUsers, currentUser.account_id, currentUser.uid],
  );
  const handleOrgChange = useCallback(
    ({ value }: { value?: string }) => {
      const nextSearchParams = new URLSearchParams(search);
      const nextAccountId = value || currentUser.account_id;
      const nextJoid = getDefaultJoidForAccount({ accountId: nextAccountId });

      if (nextAccountId) {
        nextSearchParams.set("account_id", nextAccountId);
      } else {
        nextSearchParams.delete("account_id");
      }
      if (nextJoid) {
        nextSearchParams.set("joid", nextJoid);
      } else {
        nextSearchParams.delete("joid");
      }

      navigate(
        {
          pathname,
          search: `?${nextSearchParams.toString()}`,
        },
        { replace: false },
      );
    },
    [currentUser.account_id, getDefaultJoidForAccount, navigate, pathname, search],
  );
  const handleImpersonationUserChange = useCallback(
    ({ value }: { value?: string }) => {
      setCurrentPage(1);
      setCrossAccountCursorsByPage({});
      setCrossAccountFirstPageCount(0);
      setVisibleProjectRefs([]);

      const nextSearchParams = new URLSearchParams(search);
      const nextJoid =
        value ||
        selectedAccountDefaultJoid ||
        getDefaultJoidForAccount({ accountId: selectedAccountForPicker });

      if (nextJoid) nextSearchParams.set("joid", nextJoid);

      const nextSearch = nextSearchParams.toString();
      navigate(
        {
          pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: false },
      );
    },
    [
      getDefaultJoidForAccount,
      navigate,
      pathname,
      search,
      selectedAccountDefaultJoid,
      selectedAccountForPicker,
    ],
  );

  const handleSort = useCallback((field: SortableColumnKey, order: SortOrder | null) => {
    setSortConfig(order === null ? null : { field, order });
  }, []);

  const isRowClickable = useCallback((_row: IProjectRow) => true, []);
  const orgOptions = useMemo<IFilterOption[]>(
    () =>
      getRaasOrgOptions({
        allOrgs,
        currentUserAccountId: currentUser.account_id,
        currentUserCompany: currentUser.company,
      }),
    [allOrgs, currentUser.account_id, currentUser.company],
  );
  // `getRaasUserOptions` scopes to one account for the pickers. This map is
  // unscoped, so it can still name a user that the scoping removed.
  const usersById = useMemo(
    () => getRaasUsersById({ allOrgs, allUsers }),
    [allOrgs, allUsers],
  );
  const userOptions = useMemo<IFilterOption[]>(
    () =>
      getRaasUserOptions({
        allOrgs,
        allUsers,
        canUseOrgSelector,
        currentUserAccountId: currentUser.account_id,
        currentUserEmail: currentUser.email,
        currentUserId: currentUser.uid,
        currentUserName: currentUser.name,
        selectedAccountId: selectedAccountForPicker,
      }),
    [
      allOrgs,
      allUsers,
      canUseOrgSelector,
      currentUser.account_id,
      currentUser.email,
      currentUser.name,
      currentUser.uid,
      selectedAccountForPicker,
    ],
  );
  // Unlike `clientFilterOptions` — which is state rebuilt from the current page
  // of rows — the assignee options come from the already-fetched impersonation
  // user list, so they are a plain memo covering every assignee, not just the
  // ones visible on this page.
  const assignedToFilterOptions = useMemo<IFilterOption[]>(() => {
    const allAssigneesOption: IFilterOption = {
      label: "All Assignees",
      value: ALL_ASSIGNEES_FILTER_VALUE,
    };

    if (!hasAssignedToFilterControl) return [allAssigneesOption];

    return [
      allAssigneesOption,
      ...withRaasUnresolvedUserOption({
        selectedUserId:
          assignedToFilter === ALL_ASSIGNEES_FILTER_VALUE
            ? undefined
            : assignedToFilter,
        userOptions,
        usersById,
      }),
    ];
  }, [assignedToFilter, hasAssignedToFilterControl, userOptions, usersById]);
  const toolbarControlKeys = useMemo(
    () =>
      portalDefinition.toolbarControlKeys.filter(
        (controlKey) =>
          (controlKey !== "impersonation_selector" ||
            canUseImpersonationSelector) &&
          (controlKey !== "org_selector" || canUseOrgSelector) &&
          (controlKey !== "assigned_to_filter" ||
            hasAssignedToFilterControl),
      ),
    [
      canUseImpersonationSelector,
      canUseOrgSelector,
      hasAssignedToFilterControl,
      portalDefinition.toolbarControlKeys,
    ],
  );

  return {
    accessDenied: {
      onBackToProjects: handleBackToAccessibleProjects,
      visible: isAccessDeniedError,
    },
    deleteProjectModal: {
      isDeleting: projectActions.isDeleting,
      onCancel: projectActions.closeDeleteModal,
      onConfirm: projectActions.handleDeleteConfirm,
      projectName: projectActions.projectToDeleteName,
      visible: projectActions.isDeleteModalVisible,
    },
    header: {
      attentionCountLabel,
      firstName: getFirstName({ name: currentUser.name }),
      greeting: getGreeting(),
      headlineSuffix,
      projectNoun,
    },
    projectFormModal: {
      clientOrgs: allOrgs,
      clientMode: projectActions.clientMode,
      editingProject: projectActions.editingProject,
      fieldKeys: portalDefinition.projectFormFieldKeys,
      formData: projectActions.formData,
      isCreating: projectActions.isCreating,
      isFormValid: projectActions.canSubmitProject,
      isUpdating: projectActions.isUpdating,
      onClose: projectActions.closeProjectModal,
      onClientModeChange: projectActions.handleClientModeChange,
      onSubmit: projectActions.handleSubmit,
      setFormData: projectActions.setFormData,
      userOptions,
      visible: projectActions.isProjectModalVisible,
    },
    tableSection: {
      portalId: portalDefinition.id,
      canGoNext,
      columnKeys: portalDefinition.tableColumnKeys,
      companyDetailsByMetaRef,
      filteredRowsCount: rows.length,
      isError: isProjectSearchError && !isAccessDeniedError,
      isFetching: isProjectSearchFetching,
      isLoading: isProjectSearchLoading,
      onDeleteProject: projectActions.requestDeleteProject,
      onEditProject: projectActions.openEditModal,
      onNextPage: handleNextPage,
      onPageClick: handlePageClick,
      onPreviousPage: handlePreviousPage,
      isRowClickable,
      onRowClick: handleRowClick,
      onRowKeyDown: handleRowKeyDown,
      onSort: handleSort,
      onVisibleProjectRefsChange: handleVisibleProjectRefsChange,
      paginatedRows: rows,
      rowsCount: mergedData?.projects.length ?? 0,
      safeCurrentPage,
      sortConfig,
      totalPages,
      trackVisibleRows: isCrossAccountAssigneeSearch,
      visiblePages,
    },
    toolbar: {
      assignedToFilter: {
        isLoading: isFetchingAllUsers,
        onChange: handleAssignedToFilterChange,
        options: assignedToFilterOptions,
        value: assignedToFilter,
      },
      clientFilter,
      clientFilterOptions,
      controlKeys: toolbarControlKeys,
      orgSelector: {
        allowClear:
          Boolean(selectedAccountId) &&
          selectedAccountForPicker !== currentUser.account_id,
        isLoading: isFetchingAllOrgs,
        onOrgChange: handleOrgChange,
        orgOptions,
        selectedAccountId: selectedAccountForPicker,
        visible: canUseOrgSelector,
      },
      impersonationSelector: {
        isLoading: isFetchingAllUsers,
        onUserChange: handleImpersonationUserChange,
        selectedUserId:
          selectedJoid ||
          selectedAccountDefaultJoid ||
          getDefaultJoidForAccount({ accountId: selectedAccountForPicker }),
        userOptions,
        visible: canUseImpersonationSelector,
      },
      onCreateProjectClick: projectActions.openCreateModal,
      onClientFilterChange: handleClientFilterChange,
      onSearchTermChange: handleSearchTermChange,
      onStatusFilterChange: handleStatusFilterChange,
      searchTerm,
      statusFilter,
      statusFilterOptions,
    },
  };
};
